/**
 * Authenticated chrome (sidebar + outlet) built inside CurrentUserProvider
 * so navigation can reflect role-gated admin tools.
 *
 * Navigation is both role-aware and presentation-aware. Demo mode hides
 * flag-gated entries for free (it forces every client feature flag off inside
 * `useFeatureFlag`), while the Library entry — audience-gated, no longer
 * flag-gated since the libraryCms retirement — hides via an explicit
 * `!demoMode.enabled` check below. See `useDemoMode`.
 *
 * The demo-mode role here is **presentation control, not access control** — it
 * hosts the ICT-only toggle that suppresses half-finished flag-gated surfaces
 * during a screen share; it neither grants nor withholds data access. Demo-mode
 * behavior is documented in `memory-bank/patterns/fhir-clinical.md`
 * § Feature-Flag System Pattern.
 *
 * @agentic connections
 */

import { Suspense, useMemo, type ReactNode } from "react";
import { Outlet } from "react-router";
import {
  IconBook,
  IconBuildingEstate,
  IconBuildingHospital,
  IconCalendar,
  IconHeartHandshake,
  IconListCheck,
  IconNetwork,
  IconShieldHeart,
  IconStethoscope,
  IconTarget,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { PageContentSkeleton } from "@benchmark/ui/components/atomic";
import { ConnectionsAppShell } from "@/components/common/layout/ConnectionsAppShell";
import type { ConnectionsNavMenu } from "@/components/common/layout/ConnectionsAppShell";
import { ModalProvider } from "@/app/providers";
import { AuthResetGate } from "@/app/auth/AuthResetGate";
import { useCurrentUser } from "@/app/auth/CurrentUserContext";
import { useDemoMode } from "@/hooks/shared/useDemoMode";
import { useFeatureFlag } from "@/hooks/shared/useFeatureFlag";
import { useLibraryAudience } from "@/hooks/domain/useLibraryAudience";
import { InactivityWarningModal } from "@/components/common/auth/InactivityWarningModal";

type AuthenticatedLayoutChromeProps = {
  logo: ReactNode;
  inactivityWarning: {
    opened: boolean;
    secondsUntilLogout: number;
    onExtendSession: () => void;
  };
};

export const AuthenticatedLayoutChrome = ({
  logo,
  inactivityWarning,
}: AuthenticatedLayoutChromeProps) => {
  const demoMode = useDemoMode();
  const { allowed: libraryAudienceAllowed } = useLibraryAudience();
  // The retired libraryCms flag used to hide Library during demos via flag
  // suppression; the explicit demo check preserves that behavior (internal
  // chrome stays off-screen while a clinician audience is watching).
  const libraryVisible = !demoMode.enabled && libraryAudienceAllowed;
  const bandwidthDiagnosticsEnabled = useFeatureFlag("bandwidthDiagnostics");
  const { isIctProfessional } = useCurrentUser();

  const menus = useMemo((): readonly ConnectionsNavMenu[] => {
    const baseMenus: ConnectionsNavMenu[] = [
      {
        title: "Panel",
        links: [
          { icon: <IconUsers />, label: "Members", href: "/Patient" },
          { icon: <IconListCheck />, label: "Tasks", href: "/Task" },
          {
            icon: <IconTarget />,
            label: "Triage",
            href: "/ServiceRequest/triage",
          },
        ],
      },
      {
        title: "Referrals",
        links: [
          {
            icon: <IconCalendar />,
            label: "Appointments",
            href: "/Appointment",
          },
          {
            icon: <IconHeartHandshake />,
            label: "Services",
            href: "/HealthcareService",
          },
          {
            icon: <IconBuildingHospital />,
            label: "Providers",
            href: "/Organization?type=prov",
          },
        ],
      },
      {
        title: "Insurance",
        links: [
          {
            icon: <IconShieldHeart />,
            label: "Plans",
            href: "/InsurancePlan",
          },
          {
            icon: <IconBuildingEstate />,
            label: "Payers",
            href: "/Organization?type=ins",
          },
        ],
      },
      {
        title: "Administration",
        links: [
          ...(libraryVisible
            ? [
                {
                  icon: <IconBook />,
                  label: "Library",
                  href: "/Library",
                },
              ]
            : []),
          {
            icon: <IconUsersGroup />,
            label: "Staff",
            href: "/PractitionerRole?affiliation=benchmark",
          },
          {
            icon: <IconStethoscope />,
            label: "Referrers",
            href: "/PractitionerRole?affiliation=partners",
          },
          {
            icon: <IconBuildingEstate />,
            label: "Practices",
            href: "/Organization?type=referring-practice",
          },
        ],
      },
    ];

    if (bandwidthDiagnosticsEnabled && isIctProfessional) {
      baseMenus.push({
        title: "Diagnostics",
        links: [
          {
            icon: <IconNetwork />,
            label: "Network",
            href: "/admin/network-bandwidth",
          },
        ],
      });
    }

    return baseMenus;
  }, [bandwidthDiagnosticsEnabled, isIctProfessional, libraryVisible]);

  return (
    <ConnectionsAppShell
      logo={logo}
      menus={menus}
      version={__APP_VERSION__}
      demoMode={
        demoMode.available
          ? { enabled: demoMode.enabled, onToggle: demoMode.toggle }
          : undefined
      }
    >
      <Suspense fallback={<PageContentSkeleton />}>
        <ModalProvider>
          <AuthResetGate />
          <Outlet />
        </ModalProvider>
      </Suspense>
      <InactivityWarningModal
        opened={inactivityWarning.opened}
        secondsUntilLogout={inactivityWarning.secondsUntilLogout}
        onExtendSession={inactivityWarning.onExtendSession}
      />
    </ConnectionsAppShell>
  );
};
